import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Play, Crown, Calendar, Users, Pencil, Archive, Trash2, MoreVertical } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { format } from 'date-fns';

const statusColors = {
  preparing: 'bg-slate-500/20 text-slate-200',
  in_progress: 'bg-amber-500/20 text-amber-300',
  completed: 'bg-emerald-500/20 text-emerald-300'
};

const typeColors = {
  regular: 'bg-indigo-500/20 text-indigo-300',
  emergency: 'bg-red-500/20 text-red-300',
  backlog_clearing: 'bg-cyan-500/20 text-cyan-300'
};

export default function SessionListView({ sessions, officers, onStartNew, onResume, onView, onEdit, onArchive, onDelete, userProfile }) {
  const inProgressSession = sessions.find((s) => s.status === 'in_progress');
  const isCIC = userProfile?.custom_role === 'Commander in Chief';
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'archive'|'delete', session }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <Crown className="w-7 h-7 text-amber-400" />
            High Council Sessions
          </h2>
          <p className="text-slate-300 text-sm mt-1">Structured issue review ritual</p>
        </div>
        <div className="flex gap-2">
          {inProgressSession &&
          <Button data-testid="hc-resume-session-btn" onClick={() => onResume(inProgressSession)} className="bg-amber-600 hover:bg-amber-700">
              <Play className="w-4 h-4 mr-2" /> Resume Session
            </Button>
          }
          <Button data-testid="hc-start-new-session-btn" onClick={onStartNew} className="bg-indigo-600 hover:bg-indigo-700" disabled={!!inProgressSession}>
            <Plus className="w-4 h-4 mr-2" /> Start New Session
          </Button>
        </div>
      </div>

      {sessions.length === 0 ?
      <Card className="glass-panel border-slate-800">
          <CardContent className="py-16 text-center">
            <Crown className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 text-lg mb-2">No sessions yet</p>
            <p className="text-sm text-slate-600">Start your first High Council session to begin processing the issue queue</p>
          </CardContent>
        </Card> :

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {sessions.map((session) => {
          const facilitator = officers.find((o) => o.user_email === session.facilitator);
          return (
            <Card
              key={session.id}
              className="glass-panel border-slate-800 cursor-pointer hover:border-indigo-500/30 transition-all"
              onClick={() => session.status === 'in_progress' ? onResume(session) : onView(session)}>

                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-slate-700 text-base font-semibold tracking-tight flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      {session.session_date ? format(new Date(session.session_date), 'MMM d, yyyy • h:mm a') : 'No date'}
                    </CardTitle>
                    <div className="flex gap-2">
                      <Badge className="bg-indigo-500/20 text-indigo-600 px-2.5 py-0.5 text-xs font-semibold rounded-md inline-flex items-center border transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent shadow hover:bg-primary/80">{session.session_type?.replace('_', ' ')}</Badge>
                      <Badge className="bg-amber-500/20 text-amber-600 px-2.5 py-0.5 text-xs font-semibold rounded-md inline-flex items-center border transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent shadow hover:bg-primary/80">{session.status?.replace('_', ' ')}</Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-slate-700 text-xs flex items-center gap-4">
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" /> {facilitator?.callsign || session.facilitator}
                    </span>
                    {session.status === 'completed' &&
                  <div className="flex gap-3 text-slate-200">
                        <span className="text-slate-700">Noise: {session.noise_count || 0}</span>
                        <span className="text-slate-700">Signal: {session.signal_count || 0}</span>
                        <span className="text-slate-700">🎈 {session.balloon_count || 0}</span>
                        <span className="text-slate-700">🪨 {session.rock_count || 0}</span>
                      </div>
                  }
                    {session.status === 'in_progress' &&
                  <Badge className="bg-amber-500/20 text-amber-600 px-2.5 py-0.5 text-xs font-semibold rounded-md inline-flex items-center border transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent shadow hover:bg-primary/80 animate-pulse">Phase {session.current_phase}/5</Badge>
                  }
                    {isCIC &&
                      <div data-testid="hc-session-card-menu" className="ml-auto" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-slate-200">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-slate-900 border-slate-700">
                            <DropdownMenuItem onClick={() => onEdit?.(session)} className="text-slate-300 focus:text-white focus:bg-slate-800">
                              <Pencil className="w-3.5 h-3.5 mr-2" /> Edit Session
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setConfirmAction({ type: 'archive', session })} className="text-amber-400 focus:text-amber-300 focus:bg-slate-800">
                              <Archive className="w-3.5 h-3.5 mr-2" /> Archive Session
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setConfirmAction({ type: 'delete', session })} className="text-red-400 focus:text-red-300 focus:bg-slate-800">
                              <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete Session
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    }
                  </div>
                </CardContent>
              </Card>);

        })}
        </div>
      }
      <AlertDialog open={!!confirmAction} onOpenChange={(open) => !open && setConfirmAction(null)}>
        <AlertDialogContent className="bg-slate-900 border-slate-700 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction?.type === 'delete' ? 'Delete Session' : 'Archive Session'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              {confirmAction?.type === 'delete'
                ? 'This will permanently remove this session and cannot be undone.'
                : 'This session will be archived and hidden from the main list.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={confirmAction?.type === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}
              onClick={() => {
                if (confirmAction?.type === 'delete') onDelete?.(confirmAction.session);
                else onArchive?.(confirmAction.session);
                setConfirmAction(null);
              }}
            >
              {confirmAction?.type === 'delete' ? 'Delete' : 'Archive'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>);

}